// 3rd Party
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Sticky from 'react-stickynode';

import Analytics from 'analytics-opencare';

// Components

import { SPECIALTY } from 'common/constants';
import {
  Container,
  Div,
  ResultsHeaderContainer,
  Span,
} from 'common/styles/oc-styled-components';
import { colors, NAV_HEIGHT } from 'common/styles/style-constants';
import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';
import { selectDataMatchInsuranceId, selectDataUser } from 'connect/selectors';
import { Icon } from 'opencare-crown';
import HeaderBar from 'pages/common/components/HeaderBar/HeaderBar';
import { DIMENSIONS } from 'providers/WindowDimensionsProvider';
import { useAnalyticsService } from 'services/AnalyticsService';
import scrollToRef from 'utils/scrollToRef';

import { updateSelectedAppointment } from '../../../../connect/actions';
import GoogleMaps from '../../../common/components/maps/GoogleMaps';
import { getInNetworkStatus } from '../../../common/helpers/common-helpers';
import * as Url from '../../../common/helpers/url-helpers';
import {
  selectDataClinic,
  selectDataClinicHours,
  selectDataClinicId,
  selectDataClinicProviders,
} from '../../connect/selectors';
import {
  filterInNetworkProviders,
  getMostHelpfulReview,
} from '../../helpers/profile-helpers';
import BookingWidget from '../BookingWidget/BookingWidget';
import ClinicReviews from '../ClinicReviews/ClinicReviews';
import Guarantee from '../Guarantee/Guarantee';
import Navigation from '../Navigation/Navigation';
import NoAvailableProvidersModal from '../NoAvailableProvidersModal/NoAvailableProvidersModal';
import ProfileHeaderWithBookingWidget from '../ProfileHeaderWithBookingWidget/ProfileHeaderWithBookingWidget';
import ProfileNeedToKnow from '../ProfileNeedToKnow/ProfileNeedToKnow';
import ProfileOffice from '../ProfileOffice/ProfileOffice';
import ProviderInfo from '../ProviderInfo/ProviderInfo';

// Other

import { CLINIC_STATES } from './constants';
import {
  BookingWidgetContainer,
  MobileRequestAppointmentBtnContainer,
  MobileRequestButton,
  ProfileLoadingScreen,
  ProfileSection,
  SectionDivider,
} from './styled-components';

// Component
const Profile = ({ layout, clinicIdString }) => {
  // hooks
  const dispatch = useDispatch();
  const { search } = useLocation();

  const analyticsService = useAnalyticsService();

  const [loading, setLoading] = useState(true);
  const [
    showNoAvailableProvidersModal,
    setShowNoAvailableProvidersModal,
  ] = useState(false);

  const [
    showMobileAvailabilityButton,
    setShowMobileAvailabilityButton,
  ] = useState(false);
  const [providerId, setProviderId] = useState(undefined);
  const history = useHistory();

  // selectors
  const user = useSelector(selectDataUser);
  const clinic = useSelector(selectDataClinic);
  let insurancePlanId = useSelector(selectDataMatchInsuranceId);
  const clinicHours = useSelector(selectDataClinicHours);
  const providers = useSelector(selectDataClinicProviders);
  const clinicId = useSelector(selectDataClinicId);

  if ((clinic.metadata || {}).prospectLead) {
    insurancePlanId = undefined;
  }

  // helpers
  const mostHelpfulReview = getMostHelpfulReview(clinic);
  const [offerCode] = Url.getParam(search, 'dm_promo_code');
  const showStickyButton =
    layout === DIMENSIONS.MOBILE
    && _.get(clinic, 'primarySpecialty.id') === SPECIALTY.dentist;

  // refs
  const refToBookingWidget = useRef();
  const refToProviders = useRef();
  const refToNavigation = useRef();
  const refToNeedToKnow = useRef();
  const refToOfficeDetails = useRef();
  const refToReviews = useRef();

  const analytics = new Analytics();

  useEffect(() => {
    // remove the loading screen once a clinic has been added into redux
    // checks to make sure that the clinic in redux matches the Id selected on the results card
    if (clinic && clinic.id && clinic.id === parseInt(clinicIdString)) {
      setLoading(false);
    }
  }, [clinic]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const scrollHandler = () => {
      const windowOffsetTop = window.scrollY;
      // Add offset from top of booking widget to the CTA so that sticky appears after
      const displayButtonOffset =
        _.get(refToBookingWidget, 'current.offsetTop') + 400;
      if (
        windowOffsetTop + (NAV_HEIGHT + 10)
        >= displayButtonOffset + (NAV_HEIGHT + 10)
        && _.get(clinic, 'primarySpecialty.id') === SPECIALTY.dentist
      ) {
        setShowMobileAvailabilityButton(true);
      } else {
        setShowMobileAvailabilityButton(false);
      }
    };

    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [clinic]);

  useEffect(() => {
    if (offerCode) {
      dispatch(RequestOffer.createAction(offerCode));
    }
  }, [offerCode]);

  useEffect(() => {
    if (clinic.name) {
      analytics.trackPageView({
        pageType: analytics.pageTypes.PRACTICE_PROFILE,
        pageName: clinic.name,
        name: 'match-results',
        userId: _.get(user, 'id'),
        partnerId: _.get(user, 'partner.id'),
        ...analyticsService.analyticsData,
      });
    }
  }, [clinic]);

  const inNetworkProviders = filterInNetworkProviders(
    clinicHours,
    insurancePlanId,
    providers,
  );

  const getDoctorPhoto = provider => ({
    ...provider.doctor.primaryPhoto,
    altText: provider.doctor.name,
  });
  const inNetworkProviderImages = inNetworkProviders
    ? inNetworkProviders.map(provider => getDoctorPhoto(provider))
    : [];

  useEffect(() => {
    const validStates = [CLINIC_STATES.Live, CLINIC_STATES.LiveWithWarning];
    if (
      !loading
      && !_.get(clinic, 'metadata.prospectLead')
      && (!validStates.includes(
        _.get(
          clinic,
          'accountStatus.clinic.overallStatus',
          _.get(clinic, 'currentStatus'),
        ),
      )
      || inNetworkProviders.length === 0)
    ) {
      setShowNoAvailableProvidersModal(true);
    }
  }, [inNetworkProviders]);

  // Logic added to handle when patients click the sticky button for non IB practices
  const handleRequestAppointment = () => {
    if (!inNetworkProviders || inNetworkProviders.length === 0) return;
    if (inNetworkProviders && inNetworkProviders.length === 1) {
      setProviderId(inNetworkProviders[0].id);
    }
    let providerIdRequested = '';
    if (providerId === 'all' || providerId === undefined) {
      providerIdRequested = inNetworkProviders[0].id;
    } else {
      providerIdRequested = providerId;
    }

    const anyProviderSelected =
      _.get(providers, 'length', 0) > 1
      && (providerId === 'all' || providerId === undefined);
    const selectedProvider = providers.find(
      provider => provider.id === providerIdRequested,
    );

    dispatch(
      updateSelectedAppointment(
        providerIdRequested,
        undefined,
        selectedProvider,
        anyProviderSelected,
      ),
    );

    history.push(`/results/${clinicId}/checkout`);
  };

  return (
    <>
      <header>
        <ResultsHeaderContainer>
          <HeaderBar
            pageName="profile"
            refToBookingWidget={refToBookingWidget}
            refToMainContent={refToNeedToKnow}
          />
        </ResultsHeaderContainer>
      </header>
      {loading
        ? (
          <ProfileLoadingScreen>
            <Icon type="loading" />
          </ProfileLoadingScreen>
        )
        : (
          <>
            <Helmet>
              <title>{clinic.name} | Opencare</title>
            </Helmet>
            <Div height="200px">
              <GoogleMaps singleMarker />
            </Div>
            <Container>
              <Div
                as="main"
                margin="0 0 80px"
                width={
                  layout !== DIMENSIONS.MOBILE && layout !== DIMENSIONS.TABLET
                    ? '65%'
                    : '100%'
                }
              >
                <header>
                  <ProfileHeaderWithBookingWidget
                    hasInsurance={insurancePlanId}
                    inNetwork={getInNetworkStatus(
                      insurancePlanId,
                      clinic.insurancePlans,
                    )}
                    images={inNetworkProviderImages}
                    layout={layout}
                    mostHelpfulReview={mostHelpfulReview}
                    clinic={clinic}
                    refToBookingWidget={refToBookingWidget}
                    refToReviews={refToReviews}
                  />
                </header>
                {clinic.id
                  && (
                    <Div ref={refToNavigation}>
                      <Navigation
                        layout={layout}
                        refToNeedToKnow={refToNeedToKnow}
                        refToOfficeDetails={refToOfficeDetails}
                        refToProviders={refToProviders}
                        refToReviews={refToReviews}
                      />

                      {[DIMENSIONS.MOBILE, DIMENSIONS.TABLET].indexOf(layout)
                        === -1
                        && (
                          <ProfileSection>
                            <Guarantee />
                          </ProfileSection>
                        )
                      }

                      <ProfileSection ref={refToNeedToKnow} tabIndex="-1">
                        <ProfileNeedToKnow clinic={clinic} layout={layout} />
                      </ProfileSection>

                      <SectionDivider />

                      <ProfileSection ref={refToOfficeDetails} tabIndex="-1">
                        <ProfileOffice layout={layout} />
                      </ProfileSection>

                      <SectionDivider margin="60px 0 0 0" />

                      <ProfileSection ref={refToProviders} tabIndex="-1">
                        <ProviderInfo
                          inNetworkProviders={inNetworkProviders}
                          layout={layout}
                        />
                      </ProfileSection>

                      <SectionDivider margin="60px 0 0 0" />

                      <ProfileSection
                        mobileMargin="10px 0"
                        ref={refToReviews}
                        tabIndex="-1"
                      >
                        <ClinicReviews />
                      </ProfileSection>
                      {showStickyButton
                        && (
                          <MobileRequestAppointmentBtnContainer
                            className={showMobileAvailabilityButton ? 'show' : ''}
                          >
                            <MobileRequestButton
                              size="large"
                              shape="round"
                              background={colors.coralDark}
                              color={colors.white}
                              block
                              onClick={() =>
                                clinic.isInstantBook && !user.partner
                                  ? scrollToRef(refToBookingWidget)
                                  : handleRequestAppointment()
                              }
                              data-testid="mobile-request-appt-btn"
                              className={
                                showMobileAvailabilityButton ? 'slideUp' : ''
                              }
                            >
                              <FontAwesomeIcon
                                icon={faCalendarCheck}
                                color={colors.white}
                              />
                              <Span margin="0 0 0 5px">
                                {clinic.isInstantBook && !user.partner
                                  ? `View ${clinic.slots.length} Availabilities`
                                  : 'Request Appointment'}
                              </Span>
                            </MobileRequestButton>
                          </MobileRequestAppointmentBtnContainer>
                        )
                      }
                    </Div>
                  )
                }
              </Div>
              {/* sidebar */}
              {layout !== DIMENSIONS.MOBILE && layout !== DIMENSIONS.TABLET
                && (
                  <Div
                    width="35%"
                    margin="-43px 0 0 0"
                    ref={refToBookingWidget}
                    tabIndex="-1"
                  >
                    <Sticky innerZ="20">
                      <BookingWidgetContainer>
                        <BookingWidget />
                      </BookingWidgetContainer>
                    </Sticky>
                  </Div>
                )
              }
              {/* No providers with online booking enabled modal */}
              <NoAvailableProvidersModal
                showNoAvailableProvidersModal={showNoAvailableProvidersModal}
              />
            </Container>
          </>
        )
      }
    </>
  );
};

Profile.propTypes = {
  layout: PropTypes.string,
  clinicIdString: PropTypes.string,
};

export default Profile;
